import { useSearchParams } from "react-router";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Loader2,
  OctagonAlert,
  Siren,
  Sparkles,
  Users,
} from "lucide-react";
import { useDisplayData } from "@/hooks/use-display";
import { formatTime } from "@/lib/i18n";
import type {
  DisplayQueueEntry,
  FrontDeskAlert,
  FrontDeskAppointment,
  FrontDeskData,
  FrontDeskSummary,
} from "@ease/shared";

function getAlertTone(alert: FrontDeskAlert): string {
  if (alert.severity === "critical") {
    return "border-[var(--color-destructive)]/35 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }

  if (alert.severity === "warning") {
    return "border-[var(--color-warning)]/35 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }

  return "border-[var(--color-primary)]/25 bg-[var(--color-primary)]/10 text-[var(--color-primary)]";
}

function getRiskTone(risk: FrontDeskAppointment["noShowRiskBadge"]): string {
  if (risk === "VERY_HIGH") {
    return "bg-[var(--color-destructive)]/15 text-[var(--color-destructive)]";
  }

  if (risk === "HIGH") {
    return "bg-[var(--color-warning)]/15 text-[var(--color-warning)]";
  }

  if (risk === "MEDIUM") {
    return "bg-white/10 text-[var(--color-muted-foreground)]";
  }

  return "bg-emerald-400/15 text-emerald-100";
}

function getStatusLabel(appointment: FrontDeskAppointment): string {
  if (appointment.checkedIn || appointment.status === "in_progress") {
    return "Checked in";
  }

  if (appointment.status === "requested") {
    return "Unconfirmed";
  }

  return "Expected";
}

function SummaryCard(props: {
  label: string;
  value: number;
  accent?: string;
}) {
  return (
    <article className="rounded-[1.75rem] border border-white/10 bg-white/[0.04] px-5 py-5 shadow-[0_24px_80px_rgba(0,0,0,0.22)] backdrop-blur">
      <div className="text-xs uppercase tracking-[0.22em] text-[var(--color-muted-foreground)]">{props.label}</div>
      <div className={`mt-3 text-4xl font-semibold tracking-[-0.04em] ${props.accent ?? "text-[var(--color-foreground)]"}`}>
        {props.value}
      </div>
    </article>
  );
}

function EmptyState(props: {
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-[1.5rem] border border-dashed border-white/15 bg-black/10 px-6 py-12 text-center">
      <CalendarDays className="mx-auto mb-4 h-10 w-10 text-[var(--color-muted-foreground)]" aria-hidden="true" />
      <p className="text-xl font-semibold text-[var(--color-foreground)]">{props.title}</p>
      <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">{props.body}</p>
    </div>
  );
}

export default function FrontDeskDisplay() {
  const [searchParams] = useSearchParams();
  const displayId = searchParams.get("displayId") ?? "";
  const token = searchParams.get("token") ?? "";

  const { data, isLoading, error, isStale, lastUpdatedAt } = useDisplayData<FrontDeskData>(displayId, token, "front-desk");

  if (!displayId || !token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.14),_transparent_34%),linear-gradient(180deg,color-mix(in_oklab,var(--color-background)_92%,black),var(--color-background))] p-12">
        <p className="text-2xl text-[var(--color-muted-foreground)]">Display not configured. Provide displayId and token.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.14),_transparent_34%),linear-gradient(180deg,color-mix(in_oklab,var(--color-background)_92%,black),var(--color-background))]">
        <Loader2 className="h-12 w-12 animate-spin text-[var(--color-primary)]" aria-label="Loading display data" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.14),_transparent_34%),linear-gradient(180deg,color-mix(in_oklab,var(--color-background)_92%,black),var(--color-background))] p-12">
        <div className="space-y-4 text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-[var(--color-destructive)]" aria-label="Error" />
          <p className="text-2xl text-[var(--color-destructive)]">Unable to load front desk data</p>
          <p className="text-lg text-[var(--color-muted-foreground)]">{error}</p>
        </div>
      </div>
    );
  }

  const appointments: readonly FrontDeskAppointment[] = data?.appointments ?? [];
  const alerts: readonly FrontDeskAlert[] = data?.alerts ?? [];
  const walkInQueue: readonly DisplayQueueEntry[] = data?.walkInQueue ?? [];
  const summary: FrontDeskSummary = data?.summary ?? {
    totalToday: 0,
    completed: 0,
    cancelled: 0,
    noShow: 0,
    checkedIn: 0,
    walkInWaiting: 0,
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.12),_transparent_32%),linear-gradient(180deg,color-mix(in_oklab,var(--color-background)_90%,black),var(--color-background))] px-8 py-8 text-[var(--color-foreground)] md:px-12">
      {isStale && (
        <div className="mx-auto mb-6 max-w-7xl rounded-2xl border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 px-6 py-3 text-center">
          <p className="text-lg text-[var(--color-warning)]">Data may be outdated. Reconnecting...</p>
        </div>
      )}

      <div className="mx-auto flex max-w-7xl items-end justify-between gap-6">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-[var(--color-primary)]">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Front Desk Command View
          </div>
          <h1 className="text-5xl font-semibold tracking-[-0.04em] md:text-6xl">Lobby Operations</h1>
          <p className="mt-3 max-w-3xl text-lg text-[var(--color-muted-foreground)]">
            Next 30 minutes of arrivals, walk-ins, and operational risk signals for the team at reception.
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-right backdrop-blur">
          <div className="text-xs uppercase tracking-[0.22em] text-[var(--color-muted-foreground)]">Last updated</div>
          <div className="mt-1 text-lg font-semibold">
            {lastUpdatedAt ? formatTime(lastUpdatedAt) : "..."}
          </div>
        </div>
      </div>

      <section className="mx-auto mt-8 grid max-w-7xl gap-4 md:grid-cols-3 xl:grid-cols-6">
        <SummaryCard label="Today" value={summary.totalToday} />
        <SummaryCard label="Checked In" value={summary.checkedIn} accent="text-emerald-100" />
        <SummaryCard label="Completed" value={summary.completed} />
        <SummaryCard label="Walk-ins Waiting" value={summary.walkInWaiting} accent="text-sky-100" />
        <SummaryCard label="Cancelled" value={summary.cancelled} accent="text-amber-100" />
        <SummaryCard label="No-show" value={summary.noShow} accent="text-[var(--color-destructive)]" />
      </section>

      <div className="mx-auto mt-8 grid max-w-7xl gap-6 xl:grid-cols-[1.5fr_1fr]">
        <section className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-[0_30px_90px_rgba(0,0,0,0.22)] backdrop-blur">
          <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-4">
            <div>
              <h2 className="text-2xl font-semibold tracking-[-0.03em]">Upcoming Arrivals</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Upcoming 30-minute booking window with check-in state and risk scoring.
              </p>
            </div>
            <div className="rounded-full border border-white/10 bg-black/15 px-3 py-1 text-sm text-[var(--color-muted-foreground)]">
              {appointments.length} live
            </div>
          </div>

          <div className="mt-5 grid gap-4">
            {appointments.length === 0 ? (
              <EmptyState title="No arrivals in the next 30 minutes" body="The front desk is clear for the current lookahead window." />
            ) : (
              appointments.map((appointment) => (
                <article key={appointment.id} className="rounded-[1.75rem] border border-white/10 bg-black/10 px-5 py-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-xl font-semibold text-[var(--color-primary)]">
                        {appointment.time}
                      </div>
                      <div>
                        <div className="text-2xl font-semibold tracking-[-0.03em]">{appointment.name}</div>
                        <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">{appointment.service}</div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${appointment.checkedIn ? "bg-emerald-400/15 text-emerald-100" : "bg-white/10 text-[var(--color-muted-foreground)]"}`}>
                        {getStatusLabel(appointment)}
                      </span>
                      {appointment.noShowRiskBadge ? (
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${getRiskTone(appointment.noShowRiskBadge)}`}>
                          {appointment.noShowRiskBadge.replace("_", " ")} risk
                        </span>
                      ) : null}
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>

        <div className="grid gap-6">
          <section className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-[0_30px_90px_rgba(0,0,0,0.22)] backdrop-blur">
            <div className="flex items-center gap-3">
              <Siren className="h-5 w-5 text-[var(--color-primary)]" aria-hidden="true" />
              <div>
                <h2 className="text-2xl font-semibold tracking-[-0.03em]">Attention Queue</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                  Late arrivals, elevated no-show risk, and queue pressure requiring desk attention.
                </p>
              </div>
            </div>

            <div className="mt-5 grid gap-3">
              {alerts.length === 0 ? (
                <EmptyState title="No operational alerts" body="No late arrivals or elevated queue pressure right now." />
              ) : (
                alerts.map((alert) => (
                  <article key={alert.id} className={`rounded-[1.5rem] border px-4 py-4 ${getAlertTone(alert)}`}>
                    <div className="flex items-start gap-3">
                      {alert.severity === "critical" ? (
                        <OctagonAlert className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                      ) : (
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                      )}
                      <div>
                        <div className="text-base font-semibold text-[var(--color-foreground)]">{alert.title}</div>
                        <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">{alert.detail}</div>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>

          <section className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-6 shadow-[0_30px_90px_rgba(0,0,0,0.22)] backdrop-blur">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-[var(--color-primary)]" aria-hidden="true" />
              <div>
                <h2 className="text-2xl font-semibold tracking-[-0.03em]">Walk-in Queue</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Live walk-ins currently waiting at the desk.</p>
              </div>
            </div>

            <div className="mt-5 grid gap-3">
              {walkInQueue.length === 0 ? (
                <EmptyState title="No walk-ins waiting" body="The walk-in queue is currently clear." />
              ) : (
                walkInQueue.map((entry, index) => (
                  <article key={`${entry.position}-${index}`} className="rounded-[1.5rem] border border-white/10 bg-black/10 px-4 py-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-lg font-semibold">
                          {entry.position}
                        </div>
                        <div>
                          <div className="text-lg font-semibold">{entry.name}</div>
                          <div className="text-sm text-[var(--color-muted-foreground)]">{entry.service}</div>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-xs uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">ETA</div>
                        <div className="mt-1 text-lg font-semibold">
                          {entry.estimatedWait === 0 ? "Now" : `~${entry.estimatedWait}m`}
                        </div>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>
        </div>
      </div>

      <div className="mx-auto mt-8 flex max-w-7xl items-center justify-between gap-6 rounded-[1.75rem] border border-white/10 bg-black/15 px-6 py-5 text-sm text-[var(--color-muted-foreground)]">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-4 w-4 text-emerald-200" aria-hidden="true" />
          Checked-in guests move out of the risk rail automatically.
        </div>
        <div className="flex items-center gap-3">
          <Clock3 className="h-4 w-4 text-[var(--color-primary)]" aria-hidden="true" />
          Refreshes from the mounted display runtime in real time.
        </div>
      </div>
    </div>
  );
}
